import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
  Alert,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  CalendarToday as CalendarTodayIcon,
  EventBusy as EventBusyIcon,
  Alarm as AlarmIcon,
  SupportAgent as SupportAgentIcon,
  Campaign as CampaignIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  MoneyOff as MoneyOffIcon,
  CreditCardOff as CreditCardOffIcon,
  TrendingDown as TrendingDownIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { notificationsService } from '../services/notificationsService';
import { formatRelativeTime } from '../utils/formatters';

// Mapa de tipos de notificação para ícone, cor e label (mesmo da Task 1.5)
const NOTIFICATION_TYPE_MAP = {
  'appointment.created': {
    icon: CalendarTodayIcon,
    color: '#4CAF50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    label: 'Agendamento',
  },
  'appointment.cancelled': {
    icon: EventBusyIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Cancelamento',
  },
  'appointment.reminder': {
    icon: AlarmIcon,
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    label: 'Lembrete',
  },
  'support.reply.created': {
    icon: SupportAgentIcon,
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    label: 'Suporte',
  },
  'admin.broadcast': {
    icon: CampaignIcon,
    color: '#9C27B0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
    label: 'Comunicado',
  },
  'admin.info': {
    icon: InfoIcon,
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    label: 'Informação',
  },
  'admin.alert': {
    icon: WarningIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Alerta',
  },
  'admin.maintenance': {
    icon: BuildIcon,
    color: '#757575',
    bgColor: 'rgba(117, 117, 117, 0.1)',
    label: 'Manutenção',
  },
  // ── Créditos WhatsApp (família billing.credits.*) — plano 19, TODO 1.4 ───────────────────
  // Sem entrada aqui o aviso cai no fallback cinza "Notificação". low_balance e depleted já eram
  // publicados antes do plano 19 e caíam no fallback; entram agora que a família inteira é vizinha.
  // Cores: as mesmas de appointment.reminder (aviso) e appointment.cancelled/admin.alert (erro).
  'billing.credits.low_balance': {
    icon: TrendingDownIcon,
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    label: 'Saldo baixo',
  },
  'billing.credits.depleted': {
    icon: CreditCardOffIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Saldo esgotado',
  },
  'billing.credits.message_lost': {
    icon: MoneyOffIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Sem créditos',
  },
  'billing.credits.loss_report': {
    icon: ReceiptLongIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Resumo do dia',
  },
};

const getNotificationTypeInfo = (type) => {
  return NOTIFICATION_TYPE_MAP[type] || {
    icon: InfoIcon,
    color: '#757575',
    bgColor: 'rgba(117, 117, 117, 0.1)',
    label: 'Notificação',
  };
};

/**
 * NotificationDetailModal - Modal para exibir conteudo rico de uma notificacao
 * @param {Object} props
 * @param {boolean} props.open - Se o modal esta aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {string|null} props.notificationId - ID da notificacao para buscar via API
 * @param {Object|null} props.notification - Notificacao ja carregada (evita busca)
 */
export default function NotificationDetailModal({ open, onClose, notificationId }) {
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    if (open && notificationId) {
      setLoading(true);
      notificationsService.getNotificationById(notificationId)
        .then(data => {
          setNotification(data);
          // Mark as read if unread
          if (data && !data.readAt) {
            notificationsService.markAsRead(notificationId);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [open, notificationId]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setNotification(null);
    }
  }, [open]);

  const handleActionClick = () => {
    const url = notification?.actionUrl || notification?.data?.actionUrl;
    if (url) {
      if (url.startsWith('/')) {
        window.location.hash = '';
        window.location.pathname = url;
      } else {
        window.open(url, '_blank');
      }
    }
    onClose();
  };

  const handleMarkAsRead = async () => {
    if (!notification || notification.readAt) return;
    try {
      await notificationsService.markAsRead(notification.id);
      setNotification({ ...notification, readAt: new Date().toISOString() });
    } catch {
      console.warn('[NotificationDetailModal] Error marking as read');
    }
  };

  const handleDelete = async () => {
    if (!notification) return;
    setDeleting(true);
    try {
      await notificationsService.deleteNotification(notification.id);
      setDeleteConfirm(false);
      onClose();
    } catch {
      console.warn('[NotificationDetailModal] Error deleting notification');
    } finally {
      setDeleting(false);
    }
  };

  const actionUrl = notification?.actionUrl || notification?.data?.actionUrl;
  const bodyHtml = notification?.body;
  const sanitizedBody = bodyHtml ? DOMPurify.sanitize(bodyHtml, {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'ul', 'ol', 'li', 'a', 'img', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'font', 'sub', 'sup'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'width', 'height', 'style', 'target', 'rel', 'class', 'color', 'face', 'size', 'colspan', 'rowspan']
  }) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {loading ? (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !notification ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Notificacao nao encontrada</Typography>
          <Button onClick={onClose} sx={{ mt: 2 }}>Fechar</Button>
        </Box>
      ) : (
        <>
          {/* Header com tipo, icon e status */}
          <DialogTitle sx={{ pb: 0 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              {/* Avatar com icone */}
              {(() => {
                const typeInfo = getNotificationTypeInfo(notification.type);
                const IconComponent = typeInfo.icon;
                return (
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: typeInfo.bgColor,
                      color: typeInfo.color,
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent sx={{ fontSize: 22 }} />
                  </Avatar>
                );
              })()}

              <Box sx={{ flex: 1 }}>
                {/* Titulo */}
                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                  {notification.title || 'Notificacao'}
                </Typography>

                {/* Chips: tipo + status */}
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                  {notification.type && (
                    <Chip
                      label={getNotificationTypeInfo(notification.type).label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: getNotificationTypeInfo(notification.type).bgColor,
                        color: getNotificationTypeInfo(notification.type).color,
                        fontWeight: 600,
                      }}
                    />
                  )}

                  {notification.readAt ? (
                    <Chip
                      label="Lida"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        borderColor: 'success.main',
                        color: 'success.main',
                      }}
                    />
                  ) : (
                    <Chip
                      label="Não lida"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        borderColor: 'warning.main',
                        color: 'warning.main',
                      }}
                    />
                  )}
                </Box>

                {/* Timestamp */}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, fontSize: '0.65rem' }}>
                  {formatRelativeTime(notification.createdAt)}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <Divider sx={{ my: 1.5 }} />

          {/* Conteudo */}
          <DialogContent sx={{ pt: 0 }}>
            {/* Summary/Message como fallback */}
            {!notification.body && (notification.summary || notification.message) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}
              >
                {notification.summary || notification.message}
              </Typography>
            )}

            {/* Rich HTML body */}
            {(() => {
              const bodyHtml = notification?.body;
              const sanitizedBody = bodyHtml ? DOMPurify.sanitize(bodyHtml, {
                ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'ul', 'ol', 'li', 'a', 'img', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'font', 'sub', 'sup'],
                ALLOWED_ATTR: ['href', 'src', 'alt', 'width', 'height', 'style', 'target', 'rel', 'class', 'color', 'face', 'size', 'colspan', 'rowspan']
              }) : null;

              return sanitizedBody ? (
                <Box
                  sx={{
                    '& p': { mt: 0, mb: 1 },
                    '& a': { color: 'primary.main' },
                    '& ul, & ol': { pl: 2 },
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizedBody }}
                />
              ) : null;
            })()}

            {/* Dialog de confirmacao de exclusao */}
            {deleteConfirm && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Tem certeza que deseja excluir esta notificacao?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Esta acao nao pode ser desfeita.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => setDeleteConfirm(false)}
                    disabled={deleting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Excluindo...' : 'Confirmar exclusao'}
                  </Button>
                </Box>
              </Alert>
            )}
          </DialogContent>

          <Divider sx={{ my: 1.5 }} />

          {/* Botoes de acao */}
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {/* Marcar como lida */}
            {!notification.readAt && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAsRead}
                sx={{ color: 'success.main' }}
              >
                Marcar lida
              </Button>
            )}

            {/* Ir para */}
            {(notification.actionUrl || notification.data?.actionUrl) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={handleActionClick}
              >
                Ir para
              </Button>
            )}

            {/* Excluir */}
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              color="error"
              onClick={() => setDeleteConfirm(true)}
              disabled={deleteConfirm || deleting}
            >
              Excluir
            </Button>

            {/* Fechar */}
            <Button
              size="small"
              startIcon={<CloseIcon />}
              onClick={onClose}
              disabled={deleteConfirm}
            >
              Fechar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
